import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { CheckCircle2, ChevronLeft, Loader2, Lock, PlayCircle } from 'lucide-react';
import {
    useGetTrainingCourseQuery,
    useMarkVideoWatchedMutation,
} from '../../store/api/apiSlice';
import type { TrainingVideo } from '../../types';
import { formatDuration, readableError } from './lib';
import VideoPlayer from './components/VideoPlayer';
import QuizPanel from './components/QuizPanel';
import CertificateCard from './components/CertificateCard';

const firstActionableId = (videos: TrainingVideo[]): string | null => {
    const target = videos.find((v) => !v.locked && !v.passed) ?? videos[0];
    return target ? target.id : null;
};

export default function CourseDetail() {
    const { courseId = '' } = useParams();
    const navigate = useNavigate();
    const { data: course, isLoading, isError, error } = useGetTrainingCourseQuery(courseId, { skip: !courseId });
    const [markWatched, { isLoading: isMarking }] = useMarkVideoWatchedMutation();
    const [markError, setMarkError] = useState('');

    const [selectedId, setSelectedId] = useState<string | null>(null);

    // Pick a sensible default lesson once the course loads.
    useEffect(() => {
        if (course && selectedId === null) {
            setSelectedId(firstActionableId(course.videos));
        }
    }, [course, selectedId]);

    if (isLoading) {
        return (
            <div className="flex items-center justify-center py-24">
                <Loader2 className="h-7 w-7 animate-spin text-gray-400" />
            </div>
        );
    }

    if (isError || !course) {
        return (
            <div className="p-4 md:p-8">
                <button
                    type="button"
                    onClick={() => navigate('/training')}
                    className="mb-4 inline-flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900"
                >
                    <ChevronLeft className="h-4 w-4" /> Back to training
                </button>
                <div className="rounded-2xl border border-red-200 bg-red-50 p-6 text-sm text-red-700">
                    {readableError(error, 'Could not load this course.')}
                </div>
            </div>
        );
    }

    const selected = course.videos.find((v) => v.id === selectedId) ?? null;
    const nextVideo = selected
        ? course.videos.find((v) => v.orderIndex > selected.orderIndex && !v.locked)
        : undefined;
    const pct = course.totalVideos > 0 ? Math.round((course.passedVideos / course.totalVideos) * 100) : 0;

    const handleMarkWatched = async () => {
        if (!selected) return;
        setMarkError('');
        try {
            await markWatched({ videoId: selected.id, courseId }).unwrap();
        } catch (err) {
            setMarkError(readableError(err, 'Could not mark the video as watched.'));
        }
    };

    return (
        <div className="p-4 md:p-8">
            <button
                type="button"
                onClick={() => navigate('/training')}
                className="mb-4 inline-flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900"
            >
                <ChevronLeft className="h-4 w-4" /> Back to training
            </button>

            {/* Course header */}
            <div className="mb-6">
                <div className="flex flex-wrap items-center gap-3">
                    <h1 className="text-2xl font-bold text-gray-900 md:text-3xl">{course.title}</h1>
                    {course.certified && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-green-50 px-2.5 py-1 text-xs font-medium text-green-700">
                            <CheckCircle2 className="h-3.5 w-3.5" /> Certified
                        </span>
                    )}
                </div>
                {course.description && <p className="mt-1 text-sm text-gray-500">{course.description}</p>}
                <div className="mt-4 max-w-md">
                    <div className="mb-1.5 flex items-center justify-between text-xs text-gray-500">
                        <span>
                            {course.passedVideos}/{course.totalVideos} lessons passed
                        </span>
                        <span>{pct}%</span>
                    </div>
                    <div className="h-2 w-full overflow-hidden rounded-full bg-gray-100">
                        <div
                            className={`h-full rounded-full ${course.certified ? 'bg-green-500' : 'bg-[#FF6B35]'}`}
                            style={{ width: `${pct}%` }}
                        />
                    </div>
                </div>
            </div>

            {/* Certificate (when earned) */}
            {course.certified && course.certificate && (
                <div className="mb-6">
                    <CertificateCard certificate={course.certificate} courseTitle={course.title} />
                </div>
            )}

            <div className="grid grid-cols-1 gap-6 lg:grid-cols-[320px_1fr]">
                {/* Lessons list */}
                <aside className="order-2 lg:order-1">
                    <div className="rounded-2xl border border-gray-100 bg-white p-3">
                        <p className="px-2 py-2 text-xs font-semibold uppercase tracking-wide text-gray-400">Lessons</p>
                        <ol className="space-y-1">
                            {course.videos.map((v, i) => {
                                const active = v.id === selectedId;
                                return (
                                    <li key={v.id}>
                                        <button
                                            type="button"
                                            disabled={v.locked}
                                            onClick={() => setSelectedId(v.id)}
                                            className={`flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left text-sm transition-colors ${
                                                active ? 'bg-orange-50' : 'hover:bg-gray-50'
                                            } ${v.locked ? 'cursor-not-allowed opacity-60' : ''}`}
                                        >
                                            <span className="flex h-6 w-6 shrink-0 items-center justify-center">
                                                {v.passed ? (
                                                    <CheckCircle2 className="h-5 w-5 text-green-600" />
                                                ) : v.locked ? (
                                                    <Lock className="h-4 w-4 text-gray-400" />
                                                ) : (
                                                    <PlayCircle className="h-5 w-5 text-[#FF6B35]" />
                                                )}
                                            </span>
                                            <span className="flex-1">
                                                <span className={`block ${active ? 'font-medium text-gray-900' : 'text-gray-700'}`}>
                                                    {i + 1}. {v.title}
                                                </span>
                                                <span className="text-xs text-gray-400">
                                                    {v.questionCount > 0 ? `${v.questionCount} questions` : 'No quiz'}
                                                    {formatDuration(v.durationSeconds) && ` · ${formatDuration(v.durationSeconds)}`}
                                                </span>
                                            </span>
                                        </button>
                                    </li>
                                );
                            })}
                        </ol>
                    </div>
                </aside>

                {/* Selected lesson */}
                <section className="order-1 lg:order-2">
                    {!selected ? (
                        <div className="rounded-2xl border border-gray-100 bg-white p-12 text-center text-sm text-gray-500">
                            This course has no lessons yet.
                        </div>
                    ) : selected.locked ? (
                        <div className="flex flex-col items-center rounded-2xl border border-gray-100 bg-white p-12 text-center">
                            <Lock className="mb-3 h-8 w-8 text-gray-300" />
                            <p className="font-medium text-gray-900">Lesson locked</p>
                            <p className="mt-1 text-sm text-gray-500">Pass the previous lesson to unlock this one.</p>
                        </div>
                    ) : (
                        <div className="rounded-2xl border border-gray-100 bg-white p-5 md:p-6">
                            <h2 className="mb-1 text-xl font-bold text-gray-900">{selected.title}</h2>
                            {selected.description && (
                                <p className="mb-4 text-sm text-gray-500">{selected.description}</p>
                            )}

                            <VideoPlayer video={selected} onMarkWatched={handleMarkWatched} isMarking={isMarking} />

                            {markError && (
                                <div className="mt-4 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                                    {markError}
                                </div>
                            )}

                            {/* Quiz / completion area */}
                            <div className="mt-8 border-t border-gray-100 pt-6">
                                {selected.questionCount === 0 ? (
                                    selected.passed ? (
                                        <CompletionRow nextTitle={nextVideo?.title} onNext={nextVideo ? () => setSelectedId(nextVideo.id) : undefined} />
                                    ) : (
                                        <p className="text-sm text-gray-500">
                                            This lesson has no quiz — marking it watched completes it.
                                        </p>
                                    )
                                ) : !selected.watched ? (
                                    <p className="text-sm text-gray-500">Watch the video to unlock the quiz.</p>
                                ) : selected.passed ? (
                                    <CompletionRow
                                        score={selected.bestScore}
                                        nextTitle={nextVideo?.title}
                                        onNext={nextVideo ? () => setSelectedId(nextVideo.id) : undefined}
                                    />
                                ) : (
                                    <>
                                        <h3 className="mb-4 text-lg font-semibold text-gray-900">Quiz</h3>
                                        <QuizPanel videoId={selected.id} courseId={courseId} onSubmitted={() => undefined} />
                                    </>
                                )}
                            </div>
                        </div>
                    )}
                </section>
            </div>
        </div>
    );
}

function CompletionRow({
    score,
    nextTitle,
    onNext,
}: {
    score?: number;
    nextTitle?: string | undefined;
    onNext?: (() => void) | undefined;
}) {
    return (
        <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-2 text-sm font-medium text-green-700">
                <CheckCircle2 className="h-5 w-5" />
                Lesson passed{typeof score === 'number' ? ` — best score ${score}%` : ''}.
            </div>
            {onNext && (
                <button
                    type="button"
                    onClick={onNext}
                    className="inline-flex items-center gap-2 rounded-full bg-gray-900 px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-gray-800"
                >
                    Next: {nextTitle}
                </button>
            )}
        </div>
    );
}
